class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Delegate discount handlers (works even after drawer rerenders)
    document.addEventListener('click', (e) => {
      const applyBtn = e.target.closest('[data-cart-drawer-apply-discount]');
      if (applyBtn) {
        e.preventDefault();
        this.applyDiscountFromDrawer();
        return;
      }

      const checkoutBtn = e.target.closest('#CartDrawer-Checkout');
      if (checkoutBtn) {
        const code = this.getDiscountCode();
        if (code) {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/checkout`;
        }
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-link');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => { this.classList.add('animate', 'active'); });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');

      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);

      // After opening, sync discount + hide empty upsell box
      this.syncDiscountUI();
      this.hideEmptyUpsellModule();
    }, { once: true });

  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();

      // After AJAX rerender, re-check upsell + discount UI
      this.syncDiscountUI();
      this.hideEmptyUpsellModule();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // ------------------------
  // Discount code helpers
  // ------------------------
  getDiscountCode() {
    const input = document.getElementById('CartDrawer-Discount');
    const fromInput = input ? input.value.trim() : '';
    const fromStorage = window.localStorage.getItem('us_cart_discount') || '';
    return (fromInput || fromStorage).trim();
  }

  syncDiscountUI() {
    const input = document.getElementById('CartDrawer-Discount');
    const msg = document.querySelector('.cart-drawer-discount__message');
    if (!input) return;

    const saved = (window.localStorage.getItem('us_cart_discount') || '').trim();
    if (saved && !input.value) input.value = saved;

    if (msg) msg.textContent = saved ? `Saved: ${saved}. Applies at checkout.` : '';
  }

  applyDiscountFromDrawer() {
    const input = document.getElementById('CartDrawer-Discount');
    const msg = document.querySelector('.cart-drawer-discount__message');
    if (!input) return;

    const code = input.value.trim();
    if (!code) {
      if (msg) msg.textContent = 'Enter a code.';
      return;
    }

    window.localStorage.setItem('us_cart_discount', code);
    if (msg) msg.textContent = `Saved: ${code}. Applies at checkout.`;
  }

  // ------------------------
  // Hide empty upsell module
  // ------------------------
  hideEmptyUpsellModule() {
    const wrapper = document.querySelector('cart-drawer .ProgressBarMainWrapperFirst.ProgressBarMainWrapper');
    if (!wrapper) return;

    const stack = wrapper.querySelector('.upselStackWrapper');
    const carousel = wrapper.querySelector('.carousel-inner');

    const hasStack = stack && stack.children && stack.children.length > 0;
    const hasCarousel = carousel && carousel.children && carousel.children.length > 0;

    // If empty, hide the whole “card” so you don’t get a blank box
    wrapper.style.display = (hasStack || hasCarousel) ? '' : 'none';

    // Watch for app injecting items later
    if (!wrapper.__usObserver) {
      const obs = new MutationObserver(() => {
        const hasStackNow = stack && stack.children && stack.children.length > 0;
        const hasCarouselNow = carousel && carousel.children && carousel.children.length > 0;
        wrapper.style.display = (hasStackNow || hasCarouselNow) ? '' : 'none';
      });
      obs.observe(wrapper, { childList: true, subtree: true });
      wrapper.__usObserver = obs;
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);